import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';

import type { TestedConnectSpec } from './use-tested-connect';
import type {
  ConnectionTestProblem,
  ConnectionTestResult,
  ServerTransport,
} from '@littlebird/axon-mcp';

export const XERO_TEMPLATE_ID = 'xero';

export const XERO_DEVELOPER_URL = 'https://developer.xero.com/app/manage';

export const XERO_DEFAULT_SCOPES =
  'accounting.invoices.read accounting.contacts.read accounting.settings.read';

export interface XeroCredentials {
  clientId: string;
  clientSecret: string;
  scopes: string;
}

export type XeroProblem =
  | 'credentials_rejected'
  | 'scope_refused'
  | ConnectionTestProblem;

// The connection test calls `list-organisation-details`, which first requests
// a client-credentials token and prints Xero's token error as JSON
const CREDENTIALS_REJECTED_RE = /"error":"invalid_client"/;
const SCOPE_REFUSED_RE = /"error":"invalid_scope"/;

export function classifyXeroFailure(result: ConnectionTestResult): XeroProblem {
  const problem = result.problem ?? 'connection_failed';
  if (problem !== 'connection_failed') return problem;

  const log = result.error ?? '';
  if (CREDENTIALS_REJECTED_RE.test(log)) return 'credentials_rejected';
  if (SCOPE_REFUSED_RE.test(log)) return 'scope_refused';
  return 'connection_failed';
}

export function describeXeroProblem(
  problem: XeroProblem,
  { inSetup }: { inSetup: boolean }
): string {
  switch (problem) {
    case 'credentials_rejected':
      return 'Xero rejected the client ID or secret. Copy both again from your Custom Connection.';
    case 'scope_refused':
      return `Xero refused one of the scopes. Turn on the same scopes for your Custom Connection, or remove the extra ones${inSetup ? ' under Advanced' : ''}.`;
    case 'timed_out':
      return 'Xero is taking longer than expected to connect. Try again.';
    case 'certificate_untrusted':
      return 'Littlebird couldn’t download Xero. Your network inspects secure connections with a certificate Littlebird doesn’t trust. Ask your IT team for help.';
    case 'connection_failed':
      return 'Xero couldn’t connect. Try again.';
  }
}

// xero-mcp-server prints `Name: <name> || "No name available."`, with the
// fallback inside its template string
const ORGANISATION_NAME_RE = /^Name: (.+?)(?: \|\| "No name available\.")?$/m;

export function xeroOrganisationName(
  result: ConnectionTestResult | null | undefined
): string | undefined {
  const name = ORGANISATION_NAME_RE.exec(result?.probeText ?? '')?.[1];
  return name && name !== 'undefined' ? name : undefined;
}

export const xeroScopeList = (scopes: string): string[] =>
  scopes.split(/\s+/).filter(Boolean);

export function xeroEnv(credentials: XeroCredentials): Record<string, string> {
  return {
    XERO_CLIENT_ID: credentials.clientId.trim(),
    XERO_CLIENT_SECRET: credentials.clientSecret.trim(),
    XERO_SCOPES: xeroScopeList(credentials.scopes).join(' '),
  };
}

export function xeroCredentialsFromEnv(
  env: Record<string, string> | undefined
): XeroCredentials {
  return {
    clientId: env?.XERO_CLIENT_ID ?? '',
    clientSecret: env?.XERO_CLIENT_SECRET ?? '',
    scopes: env?.XERO_SCOPES || XERO_DEFAULT_SCOPES,
  };
}

function buildXeroTransport(
  entry: OnDeviceMcpCatalogEntry,
  credentials: XeroCredentials
): ServerTransport {
  return {
    kind: 'stdio',
    runtime: entry.runtime,
    packageSpec: entry.package_spec,
    env: xeroEnv(credentials),
  };
}

export const XERO_CONNECT: TestedConnectSpec<XeroCredentials, XeroProblem> = {
  templateId: XERO_TEMPLATE_ID,
  initialConnection: {
    clientId: '',
    clientSecret: '',
    scopes: XERO_DEFAULT_SCOPES,
  },
  isComplete: (credentials) =>
    credentials.clientId.trim() !== '' &&
    credentials.clientSecret.trim() !== '' &&
    xeroScopeList(credentials.scopes).length > 0,
  buildTransport: buildXeroTransport,
  classifyFailure: classifyXeroFailure,
};
